import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { FilterChip } from '@/components/ui/filter-chip'
import { GridTable, GridTableHeader, GridTableRow } from '@/components/ui/grid-table'
import type { FaceMatchingCandidate } from '@/services/api/face-matching'
import { cn } from '@/utils/cn'
import { bindCandidateActions, type CandidateActions, CandidateDecision } from './CandidateDecision'
import { CandidateOwnerLine, candidateOwner } from './CandidateOwner'
import { FaceTile } from './FaceTile'
import { describeQualityFlags, facePageNumber, formatScore } from './format'
import { MatchScore } from './MatchScore'

// The document name keeps a floor so it cannot collapse to nothing when the
// table is narrower than its natural width (the sideways scroll below).
const READ_COLUMNS = '72px minmax(7rem,1fr) 80px minmax(0,200px) minmax(0,160px)'
// The decision column fits "merge identities" beside "reject" (ADR-0042). A
// table that offers no decision (a viewer) has no such column.
const DECISION_COLUMN = 'minmax(0,240px)'
// Below md there is no table: the header row is gone and each candidate is a
// card — the photo, and beside it the cells stacked, the decision last. The
// row's inline column template is the table's, hence the important flag.
const PHONE_ROW =
  'max-md:grid-cols-[56px_minmax(0,1fr)]! max-md:items-start max-md:gap-3 max-md:px-1 max-md:py-3'
// From md up this wrapper has no box of its own: its children are the row's cells.
const PHONE_CELLS = 'flex min-w-0 flex-col gap-1.5 md:contents'
// A cell's column name, beside its value where the header row is not shown.
const PHONE_LABEL = 'mr-1.5 text-xs text-muted-foreground md:hidden'

const base = 'cases.investigate.entityResolution.photoMatching'

/** The suggested-only filter: its state, the chips' numbers and the change. */
export interface CandidateFilter {
  suggestedOnly: boolean
  /** Unfiltered counts, so both chips keep their numbers under either filter. */
  counts: { all: number; suggested: number }
  onChange: (suggestedOnly: boolean) => void
}

export interface FaceCandidateTableProps {
  /** Suggested matches first, then by similarity — the API's order. */
  candidates: FaceMatchingCandidate[]
  /** Pending candidates matching the current filter. */
  total: number
  page: number
  pageSize: number
  filter: CandidateFilter
  onPageChange: (page: number) => void
  /**
   * Confirm, reject or merge a candidate, and the face in flight. Omitted for a
   * viewer (PRD-0032 SC-036): the table then lists the candidates without the
   * decision column.
   */
  actions?: CandidateActions
  /** Open the document detail overlay on a row's face; omitted, the rows are not clickable. */
  onOpenCandidate?: (faceId: string) => void
  /** Where an identity's page lives, so a candidate's owner links there. */
  identityHref?: (personId: string) => string
}

/**
 * The paged table of one identity's pending candidates: crop, where the face
 * came from, its similarity — with the *suggested match* label where the API
 * gave it (ADR-0040) — and quality flags. A face already confirmed to another
 * identity names it and offers the merge instead of the confirm (ADR-0042).
 * Without `actions` nothing is decided here, and the decision column is gone.
 * Pure — filter and page are state the caller owns, because they drive the query.
 */
export function FaceCandidateTable({
  candidates,
  total,
  page,
  pageSize,
  filter,
  onPageChange,
  actions,
  onOpenCandidate,
  identityHref,
}: FaceCandidateTableProps) {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const columns = actions ? `${READ_COLUMNS} ${DECISION_COLUMN}` : READ_COLUMNS

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label={t(`${base}.filter.all`)}
          count={filter.counts.all}
          active={!filter.suggestedOnly}
          onClick={() => filter.onChange(false)}
        />
        <FilterChip
          label={t(`${base}.filter.suggested`)}
          count={filter.counts.suggested}
          active={filter.suggestedOnly}
          onClick={() => filter.onChange(!filter.suggestedOnly)}
        />
      </div>

      {candidates.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{t(`${base}.noCandidates`)}</p>
      ) : (
        /* The column template needs ~58rem to stay readable (~43rem without the
           decision column), so on a narrow screen the table scrolls sideways
           like every other table of the app (`components/ui/table.tsx`) instead
           of collapsing its header columns to zero width. A phone gets cards
           instead (`PHONE_ROW`), which need no sideways scroll. */
        <GridTable columns={columns}>
          <div
            className={cn(
              'w-full overflow-x-auto',
              actions ? 'md:[&>*]:min-w-[58rem]' : 'md:[&>*]:min-w-[43rem]'
            )}
          >
            <GridTableHeader className="max-md:hidden">
              <div />
              <div>{t(`${base}.columns.source`)}</div>
              <div>{t(`${base}.columns.page`)}</div>
              <div>{t(`${base}.columns.similarity`)}</div>
              <div>{t(`${base}.columns.quality`)}</div>
              {actions ? <div>{t(`${base}.columns.decision`)}</div> : null}
            </GridTableHeader>
            <div>
              {candidates.map(candidate => (
                <CandidateRow
                  key={candidate.face_id}
                  candidate={candidate}
                  actions={actions}
                  onOpen={onOpenCandidate}
                  identityHref={identityHref}
                />
              ))}
            </div>
          </div>
        </GridTable>
      )}

      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-muted-foreground">
          {t(`${base}.pageOf`, { page, totalPages, total })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            {t(`${base}.previous`)}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            {t(`${base}.next`)}
          </Button>
        </div>
      </div>
    </div>
  )
}

function CandidateRow({
  candidate,
  actions,
  onOpen,
  identityHref,
}: {
  candidate: FaceMatchingCandidate
  actions?: CandidateActions
  onOpen?: (faceId: string) => void
  identityHref?: (personId: string) => string
}) {
  const { t } = useTranslation()
  const owner = candidateOwner(candidate, t, identityHref)

  return (
    <GridTableRow
      data-face-id={candidate.face_id}
      onClick={onOpen ? () => onOpen(candidate.face_id) : undefined}
      className={PHONE_ROW}
    >
      <FaceTile
        url={candidate.crop_url}
        alt={candidate.document_name ?? t(`${base}.columns.source`)}
        size="md"
      />
      <div className={PHONE_CELLS}>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate">
            {candidate.document_name ?? t(`${base}.unknownDocument`)}
          </span>
          {owner ? <CandidateOwnerLine owner={owner} /> : null}
        </div>
        <CandidateFacts candidate={candidate} />
        {actions ? (
          <CandidateDecision
            assigned={owner !== null}
            disabled={actions.pendingFaceId === candidate.face_id}
            on={bindCandidateActions(actions, candidate.face_id)}
            className="flex-wrap items-center gap-2 max-md:mt-1 max-md:[&>button]:h-9"
          />
        ) : null}
      </div>
    </GridTableRow>
  )
}

/**
 * Page, similarity and quality of one candidate: three cells of the table,
 * three lines of the phone card — which says nothing where the table shows a
 * dash (no page, no flags). Only a PDF's face has a page to name.
 */
function CandidateFacts({ candidate }: { candidate: FaceMatchingCandidate }) {
  const { t } = useTranslation()
  const page = facePageNumber(candidate.page, candidate.document_name)
  return (
    <>
      <div
        className={cn(
          'tabular-nums text-muted-foreground max-md:text-xs',
          page === null && 'max-md:hidden'
        )}
      >
        <span className={PHONE_LABEL}>{t(`${base}.columns.page`)}</span>
        {page ?? '—'}
      </div>
      <div>
        <span className={PHONE_LABEL}>{t(`${base}.columns.similarity`)}</span>
        <MatchScore
          score={formatScore(candidate.score)}
          suggestedMatch={candidate.suggested_match}
        />
      </div>
      <div
        className={cn(
          'truncate text-xs text-muted-foreground',
          candidate.quality_flags.length === 0 && 'max-md:hidden'
        )}
      >
        {describeQualityFlags(candidate.quality_flags, t, t(`${base}.qualityFlags.none`))}
      </div>
    </>
  )
}
